import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  MenuItem, 
  Paper, 
  Divider, 
  Alert, 
  CircularProgress, 
  IconButton,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  Stack,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Add as AddIcon, 
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CameraAlt as CameraIcon,
  QrCodeScanner as BarcodeIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Create an Axios instance with default configuration
const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '', // Use environment variable or empty string
});

// Add a request interceptor to include the auth token in all requests
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Predefined categories for new users
const PREDEFINED_CATEGORIES = [
  "Dairy", 
  "Meat", 
  "Produce", 
  "Grains", 
  "Beverages", 
  "Snacks", 
  "Canned Goods", 
  "Frozen Foods", 
  "Baking", 
  "Condiments", 
  "Spices", 
  "Hygiene", 
  "Cleaning", 
  "Pet Supplies", 
  "Office Supplies", 
  "Electronics"
];

// Styled components
const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
}));

const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: 600,
  color: theme.palette.text.primary,
  position: 'relative',
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: 0,
    width: 40,
    height: 3,
    backgroundColor: theme.palette.primary.main,
    borderRadius: 1.5,
  },
}));

const AutoFilledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'isAutoFilled',
})(({ theme, isAutoFilled }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: isAutoFilled ? 'rgba(76, 175, 80, 0.08)' : 'transparent',
    '& fieldset': {
      borderColor: isAutoFilled ? theme.palette.success.main : undefined,
      borderWidth: isAutoFilled ? 2 : undefined,
    },
    '&:hover fieldset': {
      borderColor: isAutoFilled ? theme.palette.success.dark : undefined,
    },
    '&.Mui-focused fieldset': {
      borderColor: isAutoFilled ? theme.palette.success.dark : undefined,
    },
  },
}));

const AutoFilledChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.success.light,
  color: theme.palette.success.dark,
  fontWeight: 500,
  marginLeft: theme.spacing(1),
}));

function AddEditItemPage() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isEditMode = !!itemId;

  // Initial form state
  const initialFormState = {
    item_name: '',
    description: '',
    category: '',
    quantity: '',
    unit_of_measure: '',
    purchase_date: '',
    expiry_date: '',
    location: '',
    low_stock_threshold: '',
    notes: '',
    brand: '',
    item_weight: '',
    item_weight_unit: ''
  };

  const [formData, setFormData] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [categories, setCategories] = useState([]);
  const [autoFilledFields, setAutoFilledFields] = useState([]);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategory, setNewCategory] = useState('');

  useEffect(() => {
    // Fetch categories
    async function fetchCategories() {
      try {
        console.log('Fetching categories...');
        const response = await API.get('/api/inventory/categories');
        console.log('Categories received:', response.data);
        
        // If we have categories from the backend, use those
        if (response.data && response.data.length > 0) {
          setCategories(response.data);
        } else {
          // Otherwise use predefined categories
          setCategories(PREDEFINED_CATEGORIES);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Fallback to predefined categories on error
        setCategories(PREDEFINED_CATEGORIES);
      }
    }

    fetchCategories();

    // If in edit mode, fetch item details
    if (isEditMode) {
      fetchItemDetails();
    } 
    // If prefill data is provided (from barcode scanning)
    else if (location.state?.prefillData) {
      const prefillData = location.state.prefillData;
      const filledFields = [];
      
      // Track which fields are being auto-filled
      Object.keys(prefillData).forEach(key => {
        if (prefillData[key]) {
          filledFields.push(key);
        }
      });
      
      setFormData({
        ...initialFormState,
        ...prefillData
      });
      
      setAutoFilledFields(filledFields);
      
      // Set a default category if none is provided
      if (!prefillData.category && categories.length > 0) {
        // Try to guess a category based on the product name
        const productName = prefillData.item_name?.toLowerCase() || '';
        let guessedCategory = '';
        
        // Simple category guessing logic
        if (productName.includes('milk') || productName.includes('cheese') || productName.includes('yogurt')) {
          guessedCategory = 'Dairy';
        } else if (productName.includes('bread') || productName.includes('cereal')) {
          guessedCategory = 'Grains';
        } else if (productName.includes('apple') || productName.includes('banana') || productName.includes('fruit')) {
          guessedCategory = 'Produce';
        } else if (productName.includes('vegetable') || productName.includes('carrot') || productName.includes('potato')) {
          guessedCategory = 'Produce';
        } else if (productName.includes('meat') || productName.includes('chicken') || productName.includes('beef')) {
          guessedCategory = 'Meat';
        }
        
        // If we guessed a category and it exists in our categories list
        if (guessedCategory && categories.includes(guessedCategory)) {
          setFormData(prev => ({
            ...prev,
            category: guessedCategory
          }));
          filledFields.push('category');
          setAutoFilledFields(filledFields);
        }
      }
    }
  }, [isEditMode, itemId, location.state, categories.length]);

  const fetchItemDetails = async () => {
    setIsLoading(true);
    try {
      console.log(`Fetching details for item ${itemId}...`);
      const response = await API.get(`/api/inventory/${itemId}`);
      console.log('Item details received:', response.data);

      // Format dates for the form inputs
      const item = response.data;
      if (item.purchase_date) {
        item.purchase_date = new Date(item.purchase_date).toISOString().split('T')[0];
      }
      if (item.expiry_date) {
        item.expiry_date = new Date(item.expiry_date).toISOString().split('T')[0];
      }

      setFormData(item);
    } catch (error) {
      console.error('Error fetching item details:', error);
      setErrorMessage('Failed to load item details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for category dropdown
    if (name === 'category') {
      if (value === 'new-category') {
        setShowNewCategoryInput(true);
        setNewCategory('');
      } else {
        setShowNewCategoryInput(false);
        setFormData(prevData => ({
          ...prevData,
          [name]: value
        }));
      }
    } else {
      setFormData(prevData => ({
        ...prevData,
        [name]: value
      }));
    }

    // Remove from auto-filled fields when user changes a value
    if (autoFilledFields.includes(name)) {
      setAutoFilledFields(prev => prev.filter(field => field !== name));
    }

    if (formErrors[name]) {
      setFormErrors(prevErrors => ({
        ...prevErrors,
        [name]: ''
      }));
    }
  };

  const handleNewCategoryChange = (e) => {
    const value = e.target.value;
    setNewCategory(value);
    
    // Also update the main form data
    setFormData(prevData => ({
      ...prevData,
      category: value
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.item_name.trim()) {
      errors.item_name = 'Item name is required';
    }

    if (!formData.category.trim()) {
      errors.category = 'Category is required';
    }

    if (!formData.quantity) {
      errors.quantity = 'Quantity is required';
    } else if (isNaN(Number(formData.quantity)) || Number(formData.quantity) < 0) {
      errors.quantity = 'Quantity must be a positive number';
    }

    if (!formData.unit_of_measure.trim()) {
      errors.unit_of_measure = 'Unit of measure is required';
    }

    if (formData.low_stock_threshold && (isNaN(Number(formData.low_stock_threshold)) || Number(formData.low_stock_threshold) < 0)) {
      errors.low_stock_threshold = 'Low stock threshold must be a positive number';
    }

    if (formData.purchase_date && formData.expiry_date && new Date(formData.purchase_date) > new Date(formData.expiry_date)) {
      errors.expiry_date = 'Expiry date cannot be earlier than purchase date';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    // Convert empty strings to null for certain fields
    const submitData = { ...formData };
    for (const key in submitData) {
      if (submitData[key] === '') {
        submitData[key] = null;
      }
    }

    // Convert string numbers to actual numbers
    if (submitData.quantity) submitData.quantity = Number(submitData.quantity);
    if (submitData.low_stock_threshold) submitData.low_stock_threshold = Number(submitData.low_stock_threshold);
    if (submitData.item_weight) submitData.item_weight = Number(submitData.item_weight);

    try {
      console.log(`${isEditMode ? 'Updating' : 'Creating'} inventory item:`, submitData);
      
      if (isEditMode) {
        await API.put(`/api/inventory/${itemId}`, submitData);
      } else {
        await API.post('/api/inventory', submitData);
        
        // If we added a new category, add it to our local list
        if (submitData.category && !categories.includes(submitData.category)) {
          setCategories(prev => [...prev, submitData.category]);
        }
      }

      // Redirect to inventory dashboard after success
      navigate('/inventory');
    } catch (error) {
      console.error('Error saving item:', error);
      setErrorMessage(
        error.response?.data?.message ||
        `Failed to ${isEditMode ? 'update' : 'add'} inventory item. Please try again.`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to determine if a field is auto-filled
  const isAutoFilled = (fieldName) => autoFilledFields.includes(fieldName);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading item details...
        </Typography>
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
    >
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 4,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              component={RouterLink} 
              to="/inventory" 
              sx={{ mr: 1 }}
              color="primary"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" fontWeight="bold">
              {isEditMode ? 'Edit Inventory Item' : 'Add New Inventory Item'}
            </Typography>
          </Box>
          
          {!isEditMode && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<BarcodeIcon />}
                component={RouterLink}
                to="/barcode-scan"
                size={isMobile ? 'small' : 'medium'}
              >
                Scan Barcode
              </Button>
              <Button
                variant="outlined"
                startIcon={<CameraIcon />}
                component={RouterLink}
                to="/product-recognition"
                size={isMobile ? 'small' : 'medium'}
              >
                Scan Product
              </Button>
            </Stack>
          )}
        </Box>

        {/* Error message */}
        {errorMessage && (
          <Alert 
            severity="error" 
            sx={{ mb: 3, borderRadius: 2 }}
            variant="outlined"
          >
            {errorMessage}
          </Alert>
        )}

        {/* Auto-fill notice */}
        {autoFilledFields.length > 0 && !isEditMode && (
          <Alert 
            icon={<AutoAwesomeIcon />}
            severity="success" 
            sx={{ mb: 3, borderRadius: 2 }}
            variant="outlined"
          >
            Some fields have been automatically filled based on the scanned product.
            You can edit these values if needed.
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <FormPaper elevation={2}>
            <FormSection>
              <SectionTitle variant="h6">Basic Information</SectionTitle>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <AutoFilledTextField
                    label="Item Name"
                    name="item_name"
                    value={formData.item_name}
                    onChange={handleChange}
                    error={!!formErrors.item_name}
                    helperText={formErrors.item_name}
                    required
                    fullWidth
                    isAutoFilled={isAutoFilled('item_name')}
                    InputProps={{
                      endAdornment: isAutoFilled('item_name') && (
                        <AutoFilledChip 
                          label="Auto-filled" 
                          size="small" 
                          icon={<AutoAwesomeIcon fontSize="small" />} 
                        />
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl 
                    fullWidth 
                    error={!!formErrors.category} 
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isAutoFilled('category') ? 'rgba(76, 175, 80, 0.08)' : 'transparent',
                        '& fieldset': {
                          borderColor: isAutoFilled('category') ? theme.palette.success.main : undefined,
                          borderWidth: isAutoFilled('category') ? 2 : undefined,
                        },
                      }
                    }}
                  >
                    <InputLabel id="category-label">Category</InputLabel>
                    <Select
                      labelId="category-label"
                      name="category"
                      value={showNewCategoryInput ? 'new-category' : formData.category}
                      onChange={handleChange}
                      label="Category"
                      endAdornment={isAutoFilled('category') && (
                        <AutoFilledChip 
                          label="Auto-filled" 
                          size="small" 
                          icon={<AutoAwesomeIcon fontSize="small" />} 
                        />
                      )}
                    >
                      <MenuItem value="">
                        <em>Select a category</em>
                      </MenuItem>
                      {categories.map(category => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                      <MenuItem value="new-category">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AddIcon fontSize="small" sx={{ mr: 1 }} />
                          Add new category
                        </Box>
                      </MenuItem>
                    </Select>
                    {formErrors.category && (
                      <FormHelperText>{formErrors.category}</FormHelperText>
                    )}
                  </FormControl>
                  
                  {showNewCategoryInput && (
                    <TextField
                      label="New Category Name"
                      value={newCategory}
                      onChange={handleNewCategoryChange}
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      autoFocus
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderColor: theme.palette.success.main,
                          backgroundColor: 'rgba(76, 175, 80, 0.08)',
                        }
                      }}
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <AutoFilledTextField
                    label="Description"
                    name="description"
                    value={formData.description || ''}
                    onChange={handleChange}
                    error={!!formErrors.description}
                    helperText={formErrors.description}
                    fullWidth
                    multiline
                    rows={3}
                    isAutoFilled={isAutoFilled('description')}
                    InputProps={{
                      endAdornment: isAutoFilled('description') && (
                        <AutoFilledChip 
                          label="Auto-filled" 
                          size="small" 
                          icon={<AutoAwesomeIcon fontSize="small" />} 
                        />
                      )
                    }}
                  />
                </Grid>
              </Grid>
            </FormSection>
          </FormPaper>

          {/* Quantity & Measurement */}
          <FormPaper elevation={2}>
            <FormSection>
              <SectionTitle variant="h6">Quantity & Measurement</SectionTitle>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <AutoFilledTextField
                    label="Quantity"
                    name="quantity"
                    type="number"
                    inputProps={{ min: 0, step: "any" }}
                    value={formData.quantity}
                    onChange={handleChange}
                    error={!!formErrors.quantity}
                    helperText={formErrors.quantity}
                    required
                    fullWidth
                    isAutoFilled={isAutoFilled('quantity')}
                    InputProps={{
                      endAdornment: isAutoFilled('quantity') && (
                        <AutoFilledChip 
                          label="Auto-filled" 
                          size="small" 
                          icon={<AutoAwesomeIcon fontSize="small" />} 
                        />
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <AutoFilledTextField
                    label="Unit of Measure"
                    name="unit_of_measure"
                    placeholder="e.g., units, oz, kg, etc."
                    value={formData.unit_of_measure}
                    onChange={handleChange}
                    error={!!formErrors.unit_of_measure}
                    helperText={formErrors.unit_of_measure}
                    required
                    fullWidth
                    isAutoFilled={isAutoFilled('unit_of_measure')}
                    InputProps={{
                      endAdornment: isAutoFilled('unit_of_measure') && (
                        <AutoFilledChip 
                          label="Auto-filled" 
                          size="small" 
                          icon={<AutoAwesomeIcon fontSize="small" />} 
                        />
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <AutoFilledTextField
                    label="Low Stock Threshold"
                    name="low_stock_threshold"
                    type="number"
                    inputProps={{ min: 0 }}
                    placeholder="Notify when quantity falls below this value"
                    value={formData.low_stock_threshold || ''}
                    onChange={handleChange}
                    error={!!formErrors.low_stock_threshold}
                    helperText={formErrors.low_stock_threshold || "Optional: Set a threshold for low stock alerts"}
                    fullWidth
                    isAutoFilled={isAutoFilled('low_stock_threshold')}
                    InputProps={{
                      endAdornment: isAutoFilled('low_stock_threshold') && (
                        <AutoFilledChip 
                          label="Auto-filled" 
                          size="small" 
                          icon={<AutoAwesomeIcon fontSize="small" />} 
                        />
                      )
                    }}
                  />
                </Grid>
              </Grid>
            </FormSection>
          </FormPaper>

          {/* Dates & Location */}
          <FormPaper elevation={2}>
            <FormSection>
              <SectionTitle variant="h6">Dates & Location</SectionTitle>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <AutoFilledTextField
                    label="Purchase Date"
                    name="purchase_date"
                    type="date"
                    value={formData.purchase_date || ''}
                    onChange={handleChange}
                    error={!!formErrors.purchase_date}
                    helperText={formErrors.purchase_date}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    isAutoFilled={isAutoFilled('purchase_date')}
                    InputProps={{
                      endAdornment: isAutoFilled('purchase_date') && (
                        <AutoFilledChip 
                          label="Auto-filled" 
                          size="small" 
                          icon={<AutoAwesomeIcon fontSize="small" />} 
                        />
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <AutoFilledTextField
                    label="Expiry Date"
                    name="expiry_date"
                    type="date"
                    value={formData.expiry_date || ''}
                    onChange={handleChange}
                    error={!!formErrors.expiry_date}
                    helperText={formErrors.expiry_date}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    isAutoFilled={isAutoFilled('expiry_date')}
                    InputProps={{
                      endAdornment: isAutoFilled('expiry_date') && (
                        <AutoFilledChip 
                          label="Auto-filled" 
                          size="small" 
                          icon={<AutoAwesomeIcon fontSize="small" />} 
                        />
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <AutoFilledTextField
                    label="Storage Location"
                    name="location"
                    placeholder="e.g., Pantry, Refrigerator, Bathroom, etc."
                    value={formData.location || ''}
                    onChange={handleChange}
                    error={!!formErrors.location}
                    helperText={formErrors.location}
                    fullWidth
                    isAutoFilled={isAutoFilled('location')}
                    InputProps={{
                      endAdornment: isAutoFilled('location') && (
                        <AutoFilledChip 
                          label="Auto-filled" 
                          size="small" 
                          icon={<AutoAwesomeIcon fontSize="small" />} 
                        />
                      )
                    }}
                  />
                </Grid>
              </Grid>
            </FormSection>
          </FormPaper>

          {/* Additional Information */}
          <FormPaper elevation={2}>
            <FormSection>
              <SectionTitle variant="h6">Additional Information</SectionTitle>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <AutoFilledTextField
                    label="Brand"
                    name="brand"
                    value={formData.brand || ''}
                    onChange={handleChange}
                    error={!!formErrors.brand}
                    helperText={formErrors.brand}
                    fullWidth
                    isAutoFilled={isAutoFilled('brand')}
                    InputProps={{
                      endAdornment: isAutoFilled('brand') && (
                        <AutoFilledChip 
                          label="Auto-filled" 
                          size="small" 
                          icon={<AutoAwesomeIcon fontSize="small" />} 
                        />
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <AutoFilledTextField
                    label="Item Weight"
                    name="item_weight"
                    type="number"
                    inputProps={{ min: 0, step: "any" }}
                    value={formData.item_weight || ''}
                    onChange={handleChange}
                    error={!!formErrors.item_weight}
                    helperText={formErrors.item_weight}
                    fullWidth
                    isAutoFilled={isAutoFilled('item_weight')}
                    InputProps={{
                      endAdornment: isAutoFilled('item_weight') && (
                        <AutoFilledChip 
                          label="Auto-filled" 
                          size="small" 
                          icon={<AutoAwesomeIcon fontSize="small" />} 
                        />
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <AutoFilledTextField
                    label="Weight Unit"
                    name="item_weight_unit"
                    placeholder="e.g., g, kg, oz, lb"
                    value={formData.item_weight_unit || ''}
                    onChange={handleChange}
                    error={!!formErrors.item_weight_unit}
                    helperText={formErrors.item_weight_unit}
                    fullWidth
                    isAutoFilled={isAutoFilled('item_weight_unit')}
                    InputProps={{
                      endAdornment: isAutoFilled('item_weight_unit') && (
                        <AutoFilledChip 
                          label="Auto-filled" 
                          size="small" 
                          icon={<AutoAwesomeIcon fontSize="small" />} 
                        />
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <AutoFilledTextField
                    label="Notes"
                    name="notes"
                    placeholder="Any additional information about this item"
                    value={formData.notes || ''}
                    onChange={handleChange}
                    error={!!formErrors.notes}
                    helperText={formErrors.notes}
                    fullWidth
                    multiline
                    rows={3}
                    isAutoFilled={isAutoFilled('notes')}
                    InputProps={{
                      endAdornment: isAutoFilled('notes') && (
                        <AutoFilledChip 
                          label="Auto-filled" 
                          size="small" 
                          icon={<AutoAwesomeIcon fontSize="small" />} 
                        />
                      )
                    }}
                  />
                </Grid>
              </Grid>
            </FormSection>
          </FormPaper>

          {/* Form Actions */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 2, 
            mt: 4,
            mb: 4,
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              startIcon={<CancelIcon />}
              onClick={() => navigate('/inventory')}
              fullWidth={isMobile}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              startIcon={<SaveIcon />}
              disabled={isSubmitting}
              fullWidth={isMobile}
            >
              {isSubmitting
                ? (isEditMode ? 'Updating...' : 'Adding...')
                : (isEditMode ? 'Update Item' : 'Add Item')}
            </Button>
          </Box>
        </form>
      </Box>
    </motion.div>
  );
}

export default AddEditItemPage;